import {
  CALENDAR_WALK_DAYS,
  isWeekend,
  type MarketCalendar,
  type Session,
  type SessionPhase,
  VENUE_LABEL,
  type Venue,
} from './calendar.ts';
import { counted } from './primitives.ts';
import {
  addDays,
  daysBetween,
  istClock,
  todayInIst,
  weekdayLongSaid,
  weekdaySaid,
} from './when.ts';

const HOLIDAY_NOTICE_DAYS = 30;

export const spanOf = (one: SessionPhase): string => `${istClock(one.from)}–${istClock(one.to)}`;

export const normalOf = (session: Session): SessionPhase[] =>
  session.phases.filter((one) => one.phase === 'NORMAL');

export const hoursOf = (session: Session): string => normalOf(session).map(spanOf).join(', ');

export const whenSaid = (day: string, today: string): string =>
  day === today ? 'today' : day === addDays(today, 1) ? 'tomorrow' : `on ${weekdayLongSaid(day)}`;

/** A holiday on a Sunday is no news. */
export function newsOf(calendar: MarketCalendar, venue: Venue, day: string) {
  const kept = calendar.kept(venue, day);
  return kept && !(isWeekend(day) && kept.keep === 'SHUT') ? kept : null;
}

export function firstDay(from: string, found: (day: string) => boolean): string | null {
  for (let step = 0; step <= CALENDAR_WALK_DAYS; step += 1) {
    const day = addDays(from, step);
    if (found(day)) return day;
  }
  return null;
}

const TRADES_VENUES: readonly Venue[] = ['NFO', 'MCX'];

const nextMarketDay = (calendar: MarketCalendar, today: string): string | null =>
  firstDay(
    addDays(today, 1),
    (day) => !isWeekend(day) || TRADES_VENUES.some((venue) => newsOf(calendar, venue, day)),
  );

function clauseOf(calendar: MarketCalendar, venue: Venue, day: string): string {
  const label = VENUE_LABEL[venue];
  const session = calendar.session(venue, day);
  if (session === null) {
    return calendar.kept(venue, day)?.keep === 'SPECIAL'
      ? `${label} opens for a special session`
      : `${label} shut`;
  }
  switch (calendar.kept(venue, day)?.keep) {
    case 'EVENING_ONLY':
      return `${label} opens ${istClock(session.open)}`;
    case 'MORNING_ONLY':
      return `${label} closes ${istClock(session.close)}`;
    default:
      return `${label} trades ${hoursOf(session)}`;
  }
}

function lineFor(calendar: MarketCalendar, day: string, when: string): string | null {
  const news = TRADES_VENUES.flatMap((venue) => {
    const kept = newsOf(calendar, venue, day);
    return kept ? [{ venue, kept }] : [];
  });
  const [first] = news;
  if (first === undefined) return null;
  if (news.every((one) => one.kept.keep === 'SHUT')) {
    const names = [...new Set(news.map((one) => one.kept.name))].join(' · ');
    return `${news.map((one) => VENUE_LABEL[one.venue]).join(' and ')} shut ${when} — ${names}`;
  }
  return news
    .map((one) => {
      const clause = clauseOf(calendar, one.venue, day);
      if (one === first) return `${clause} ${when} — ${one.kept.name}`;
      return one.kept.name === first.kept.name ? clause : `${clause} — ${one.kept.name}`;
    })
    .join(' · ');
}

export function todayLine(calendar: MarketCalendar, now: Date): string | null {
  const today = todayInIst(now);
  const line = lineFor(calendar, today, 'today');
  if (line !== null) return line;
  const next = nextMarketDay(calendar, today);
  if (next === null) return null;
  return lineFor(calendar, next, whenSaid(next, today));
}

export function holidayAhead(calendar: MarketCalendar, now: Date): string | null {
  const today = todayInIst(now);
  const said = lineFor(calendar, today, 'today') === null ? nextMarketDay(calendar, today) : today;
  const until = addDays(today, HOLIDAY_NOTICE_DAYS);
  for (let day = addDays(said ?? today, 1); day <= until; day = addDays(day, 1)) {
    if (isWeekend(day)) continue;
    const news = TRADES_VENUES.flatMap((venue) => {
      const kept = newsOf(calendar, venue, day);
      return kept && kept.keep !== 'SPECIAL' ? [{ venue, kept }] : [];
    });
    if (news.length === 0) continue;
    const names = [...new Set(news.map((one) => one.kept.name))].join(' · ');
    const clauses = news.every((one) => one.kept.keep === 'SHUT')
      ? `${news.map((one) => VENUE_LABEL[one.venue]).join(' and ')} shut`
      : news.map((one) => clauseOf(calendar, one.venue, day)).join(' · ');
    const days = daysBetween(today, day);
    const when = `${weekdaySaid(day)}, in ${counted(days, 'day', 'days')}`;
    return `${names} on ${when}: ${clauses}`;
  }
  return null;
}
